<template>
    <div class="purchase-order-form">
        <vx-card :title="editData ? 'Edit Purchase Order' : 'Create Purchase Order'" class="mb-base">
            <vs-row>
                <!-- Header Fields -->
                <vs-col vs-xs="12" vs-sm="4" class="mb-3 px-2">
                    <vs-input
                        v-model="formData.po_number"
                        label="PO Number *"
                        placeholder="Enter PO Number"
                        class="w-full"
                        :disabled="isApproved"
                    />
                    <span class="text-danger text-sm" v-if="validationErrors.po_number">{{ validationErrors.po_number.join(', ') }}</span>
                </vs-col>

                <vs-col vs-xs="12" vs-sm="4" class="mb-3 px-2">
                    <SelectForeign
                        :model.sync="formData.vendor"
                        label="Vendor ID / Vendor Name *"
                        placeholder="Select Vendor"
                        :multiple="false"
                        :load_initial="true"
                        :label_keys="['code', 'name']"
                        label_separator="/"
                        :show_id_in_label="false"
                        :endpoint="vendorEndpoint"
                        container_class="w-full"
                        :use_id_as_value="true"
                        :disabled="isApproved"
                        :result_filter="item => item.is_active"
                    />
                    <span class="text-danger text-sm" v-if="validationErrors.vendor">{{ validationErrors.vendor.join(', ') }}</span>
                </vs-col>
                
                <vs-col vs-xs="12" vs-sm="4" class="mb-3 px-2">
                    <SelectForeign
                        :model.sync="formData.fy"
                        label="Financial Year *"
                        placeholder="Select FY"
                        :multiple="false"
                        :load_initial="true"
                        :label_keys="['fy_year']"
                        :show_id_in_label="false"
                        :endpoint="fyEndpoint"
                        container_class="w-full"
                        :use_id_as_value="true"
                        :disabled="isApproved"
                    />
                    <span class="text-danger text-sm" v-if="validationErrors.fy">{{ validationErrors.fy.join(', ') }}</span>
                </vs-col>

                <vs-col vs-xs="12" vs-sm="4" class="mb-3 px-2">
                    <SelectDate 
                        :selectedDate.sync="formData.po_date" 
                        label="PO Date *"
                        class="w-full"
                        :disabled="isApproved"
                    />
                    <span class="text-danger text-sm" v-if="validationErrors.po_date">{{ validationErrors.po_date.join(', ') }}</span>
                </vs-col>
                
                <vs-col vs-xs="12" vs-sm="12" class="mb-3 px-2">
                    <vs-input
                        v-model="formData.remark"
                        label="Remarks"
                        placeholder="Enter remarks"
                        class="w-full"
                        :disabled="isApproved"
                    />
                </vs-col>
            </vs-row>

            <vs-divider>Order Details</vs-divider>

            <div class="flex flex-wrap items-center justify-end mt-6 mb-2" v-if="!isApproved">
                <vs-button size="small" icon="icon-plus" icon-pack="feather" @click="addItem">Add Item</vs-button>
            </div>

            <!-- Multiple Item Details Table -->
            <div class="vs-component vs-con-table stripe vs-table-primary order-items-table">
                <div class="vs-con-tbody vs-table--tbody ">
                    <table class="vs-table vs-table--tbody-table">
                        <thead class="vs-table--thead">
                            <tr>
                                <th class="py-2 px-2 text-sm">item sub group code</th>
                                <th class="py-2 px-2 text-sm">Description</th>
                                <th class="py-2 px-2 text-sm" style="width: 100px">UOM</th>
                                <th class="py-2 px-2 text-sm" style="width: 100px">Qty</th>
                                <th class="py-2 px-2 text-sm text-right" style="width: 150px">Unit rate</th>
                                <th class="py-2 px-2 text-sm text-right" style="width: 120px">Gst%</th>
                                <th class="py-2 px-2 text-sm text-right" style="width: 150px">Amount</th>
                                <th class="py-2 px-2 text-sm" style="width: 40px"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in paginatedItems" :key="item.tempId || index">
                                <td class="py-1 px-1">
                                    <v-select
                                        :value="item.item_sub_group"
                                        :options="getAvailableSubGroupOptions(item)"
                                        label="code"
                                        :reduce="option => option.id"
                                        :clearable="false"
                                        placeholder="Select Group"
                                        class="w-full"
                                        :disabled="isApproved"
                                        @input="onItemSubGroupChange(item, $event)"
                                    ></v-select>
                                </td>
                                <td class="py-1 px-1">
                                    <vs-input v-model="item.description" class="w-full bg-gray-100" placeholder="Enter description" readonly />
                                </td>
                                <td class="py-1 px-1">
                                    <vs-input :value="item.uom_display" readonly class="w-full bg-gray-100" />
                                </td>
                                <td class="py-1 px-1">
                                    <vs-input type="number" v-model.number="item.qty" class="w-full" min="1" :disabled="isApproved" />
                                </td>
                                <td class="py-1 px-1">
                                    <vs-input type="number" step="0.01" v-model.number="item.unit_rate" class="w-full text-right" :disabled="isApproved" />
                                </td>
                                <td class="py-1 px-1">
                                    <vs-input type="number" step="0.01" v-model.number="item.gst_percent" class="w-full text-right" :disabled="isApproved" />
                                </td>
                                <td class="py-1 px-1 text-right font-bold">
                                    {{ calculateAmount(item) }}
                                </td>
                                <td class="py-1 px-1 text-center">
                                    <vs-button v-if="item.id || isApproved" radius color="success" type="flat" size="small" icon="icon-check" icon-pack="feather" disabled></vs-button>
                                    <vs-button v-else radius color="danger" type="flat" size="small" icon="icon-x" icon-pack="feather" @click="removeItem(index)"></vs-button>
                                </td>
                            </tr>
                            <!-- Total Row & Pagination -->
                            <tr>
                                <td colspan="6" class="py-2 px-2 text-right font-bold">Total Amount</td>
                                <td class="py-2 px-2 text-right font-bold">{{ totalAmount }}</td>
                                <td></td>
                            </tr>
                            <tr v-if="items.length === 0">
                                <td colspan="7" class="text-center py-4 text-muted">No items added. Click 'Add Item' to start.</td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <div class="mt-4" v-if="totalPages > 1">
                         <vs-pagination :total="totalPages" v-model="currentPage"></vs-pagination>
                    </div>
                </div>
            </div>
            


            <div class="actions text-right mt-6">
                <vs-button type="border" color="warning" class="mr-2" @click="$emit('cancel')">Cancel</vs-button>
                <vs-button v-if="!isApproved" @click="submit" :disabled="submitting">{{ editData ? 'Update Purchase Order' : 'Create Purchase Order' }}</vs-button>
            </div>
        </vx-card>
    </div>
</template>

<script>
import { Request } from '../../utils/requests';
import vSelect from 'vue-select';

export default {
    props: {
        editData: {
            type: Object,
            default: null
        }
    },
    components: {
        'v-select': vSelect
    },
    data() {
        return {
            formData: {
                po_number: '',
                vendor: null,
                fy: null,
                po_date: null,
                remark: '',
            },
            // Maintain items list
            items: [
                {
                    item_sub_group: null,
                    description: '',
                    uom: null,
                    uom_display: '',
                    qty: 1,
                    unit_rate: 0,
                    gst_percent: 0,
                    id: null,
                    tempId: 'new-init'
                }
            ],
            currentPage: 1,
            itemsPerPage: 5,
            validationErrors: {},
            submitting: false,
            itemSubGroupOptions: []
        };
    },
    computed: {
        vendorEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_vendor}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_vendor}?is_active=true`;
        },
        fyEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_year}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_year}?is_active=true`;
        },
        itemSubGroupEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_item_sub_group}?organisation=${orgId}&is_active=true` : this.$store.state.apiendpoints.master_item_sub_group;
        },
        uomEndpoint() {
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            return orgId ? `${this.$store.state.apiendpoints.master_uom}?organisation=${orgId}&is_active=true` : `${this.$store.state.apiendpoints.master_uom}?is_active=true`;
        },
        totalAmount() {
            return this.items.reduce((acc, item) => {
                const qty = parseFloat(item.qty) || 0;
                const rate = parseFloat(item.unit_rate) || 0;
                const gst = parseFloat(item.gst_percent) || 0;
                return acc + (qty * rate * (1 + gst / 100));
            }, 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        paginatedItems() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            return this.items.slice(start, start + this.itemsPerPage);
        },
        totalPages() {
            return Math.ceil(this.items.length / this.itemsPerPage);
        },
        isApproved() {
            return this.editData && this.editData.status && this.editData.status.toLowerCase() === 'approved';
        }
    },
    mounted() {
        // Fetch Item Sub Groups once with merge logic
        let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
        if (orgId) {
            // Try to include UOM details in the response
            let isgUrl = this.$store.state.apiendpoints.master_item_sub_group + '?limit=1000&is_active=true&organisation=' + orgId + '&include=uom';
            Request(this, 'GET', isgUrl).then(res => {
                const newOptions = res.results || res;
                // Merge options to preserve inactive items from initEditData
                const currentOptions = this.itemSubGroupOptions;
                const toAdd = newOptions.filter(opt => !currentOptions.some(o => o.id === opt.id));
                
                if (toAdd.length > 0) {
                    this.itemSubGroupOptions = [...currentOptions, ...toAdd];
                }
            }).catch(err => {
                // Fallback without include parameter
                let fallbackUrl = this.$store.state.apiendpoints.master_item_sub_group + '?limit=1000&is_active=true&organisation=' + orgId;
                Request(this, 'GET', fallbackUrl).then(res => {
                    const newOptions = res.results || res;
                    const currentOptions = this.itemSubGroupOptions;
                    const toAdd = newOptions.filter(opt => !currentOptions.some(o => o.id === opt.id));
                    
                    if (toAdd.length > 0) {
                        this.itemSubGroupOptions = [...currentOptions, ...toAdd];
                    }
                });
            });
        }

        if (this.editData) {
            this.initEditData();
        }
    },
    watch: {
        editData: function(val) {
            if (val) this.initEditData();
            else this.resetForm();
        }
    },
    methods: {
        resetForm() {
            this.formData = {
                po_number: '',
                vendor: null,
                fy: null,
                po_date: null,
                remark: '',
            };
            this.items = [
                {
                    item_sub_group: null,
                    description: '',
                    uom: null,
                    uom_display: '',
                    qty: 1,
                    unit_rate: 0,
                    gst_percent: 0,
                    id: null,
                    tempId: 'new-reset'
                }
            ];
        },
        getAvailableSubGroupOptions(currentItem) {
            const selectedIds = this.items
                .filter(i => i !== currentItem && i.item_sub_group != null)
                .map(i => i.item_sub_group);
            return this.itemSubGroupOptions.filter(opt => !selectedIds.includes(opt.id));
        },
        addItem() {
            this.items.push({
                item_sub_group: null,
                description: '',
                uom: null,
                uom_display: '',
                qty: 1,
                unit_rate: 0,
                gst_percent: 0,
                id: null,
                tempId: 'new-' + Date.now()
            });
            // Switch to last page
            this.$nextTick(() => {
                this.currentPage = this.totalPages;
            });
        },
        removeItem(index) {
            const globalIndex = (this.currentPage - 1) * this.itemsPerPage + index;
            this.items.splice(globalIndex, 1);
            // If page becomes empty, go back
            if (this.paginatedItems.length === 0 && this.currentPage > 1) {
                this.currentPage--;
            }
        },
        calculateAmount(item) {
            const qty = parseFloat(item.qty) || 0;
            const rate = parseFloat(item.unit_rate) || 0;
            const gst = parseFloat(item.gst_percent) || 0;
            return (qty * rate * (1 + gst / 100)).toFixed(2);
        },
        initEditData() {
            if (!this.editData || !this.editData.id) return;

            this.$vs.loading();
            // Ensure proper URL formatting
            let baseUrl = this.$store.state.apiendpoints.purchase_order;
            if (!baseUrl.endsWith('/')) baseUrl += '/';
            let url = `${baseUrl}${this.editData.id}/`;

            Request(this, 'GET', url).then(res => {
                this.$vs.loading.close();
                const data = res.data || res;
                
                this.formData.po_number = data.po_number || '';
                this.formData.vendor = data.vendor ? (data.vendor.id || data.vendor) : null;
                this.formData.fy = data.fy ? (data.fy.id || data.fy) : null;
                this.formData.po_date = data.po_date;
                this.formData.remark = data.remark;

                if (data.details && data.details.length > 0) {
                    this.items = data.details.map(item => {
                        // Ensure Item Sub Group is available in options for display
                        // Ensure Item Sub Group is available in options for display
                        let isgData = item.item_sub_group_detail || item.item_sub_group;
                        if (isgData) {
                            const idToCheck = (typeof isgData === 'object') ? isgData.id : isgData;
                            const exists = this.itemSubGroupOptions.some(opt => opt.id === idToCheck);
                            
                            if (!exists) {
                                if (typeof isgData === 'object' && isgData.id) {
                                    this.itemSubGroupOptions.push(isgData);
                                } else {
                                    // Only ID available, and not in options. Fetch it to get label.
                                    let url = this.$store.state.apiendpoints.master_item_sub_group + idToCheck + '/';
                                    Request(this, 'GET', url).then(subRes => {
                                        if (subRes && subRes.id) {
                                            const subExists = this.itemSubGroupOptions.some(o => o.id === subRes.id);
                                            if (!subExists) {
                                                this.itemSubGroupOptions.push(subRes);
                                            }
                                        }
                                    });
                                }
                            }
                        }

                        let newItem = {
                            id: item.id,
                            item_sub_group: isgData ? (isgData.id || isgData) : null,
                            description: (typeof isgData === 'object' && isgData.description) ? isgData.description : '',
                            // Use item UOM, fallback to sub-group UOM
                            uom: item.uom ? (item.uom.id || item.uom) : (isgData && isgData.uom ? (isgData.uom.id || isgData.uom) : null),
                            uom_display: '',
                            qty: parseFloat(item.qty),
                            unit_rate: parseFloat(item.unit_rate),
                            gst_percent: parseFloat(item.gst_percent) || 0,
                            tempId: 'existing-' + item.id
                        };

                        // UOM Logic: Trust that PO details have the code
                        if (item.uom && item.uom.code) {
                            newItem.uom_display = item.uom.code;
                        } else if (item.item_sub_group_detail && item.item_sub_group_detail.uom_detail && item.item_sub_group_detail.uom_detail.code) {
                            // Extract from nested detail as per user request
                            newItem.uom_display = item.item_sub_group_detail.uom_detail.code;
                            if (!newItem.uom) {
                                newItem.uom = item.item_sub_group_detail.uom_detail.id;
                            }
                        }

                        return newItem;
                    });

                    // Force reactivity update for dropdowns
                    this.itemSubGroupOptions = [...this.itemSubGroupOptions];
                } else {
                     // Empty items if none
                     this.items = [];
                }
            }).catch(err => {
                this.$vs.loading.close();
                this.$vs.notify({ title: 'Error', text: 'Failed to fetch PO details', color: 'danger' });
                console.error(err);
            });
        },
        onItemSubGroupChange(item, val) {
            // Duplicate check: ensure the same item sub group isn't already in another row
            if (val) {
                const isDuplicate = this.items.some(i => i !== item && i.item_sub_group === val);
                if (isDuplicate) {
                    const code = (this.itemSubGroupOptions.find(o => o.id === val) || {}).code || val;
                    this.$vs.notify({
                        title: 'Duplicate Item',
                        text: `Item Sub Group "${code}" is already added. Each item sub group can appear only once per PO.`,
                        color: 'warning',
                        time: 5000
                    });
                    return; // Don't apply the change
                }
            }
            item.item_sub_group = val;
            if (!item.item_sub_group) {
                item.description = '';
                item.uom = null;
                item.uom_display = '';
                return;
            }
            // Use local options to find UOM/description if possible to avoid call
            const selected = this.itemSubGroupOptions.find(opt => opt.id === item.item_sub_group);
            if (selected) {
                if (selected.description) {
                    item.description = selected.description;
                }
                // Check for UOM in multiple possible formats from cached list
                let uomFound = false;
                if (selected.uom) {
                    uomFound = true;
                    const uomData = selected.uom;
                    item.uom = (typeof uomData === 'object') ? uomData.id : uomData;
                    if (typeof uomData === 'object' && uomData.code) {
                        item.uom_display = uomData.code;
                    } else if (typeof uomData === 'object' && uomData.name) {
                        item.uom_display = uomData.name;
                    } else {
                        this.fetchUomCode(item);
                    }
                } else if (selected.unit_of_measurement) {
                    uomFound = true;
                    const uomData = selected.unit_of_measurement;
                    item.uom = (typeof uomData === 'object') ? uomData.id : uomData;
                    if (typeof uomData === 'object' && uomData.code) {
                        item.uom_display = uomData.code;
                    } else if (typeof uomData === 'object' && uomData.name) {
                        item.uom_display = uomData.name;
                    } else {
                        this.fetchUomCode(item);
                    }
                } else if (selected.uom_detail) {
                    uomFound = true;
                    const uomData = selected.uom_detail;
                    item.uom = (typeof uomData === 'object') ? uomData.id : uomData;
                    if (typeof uomData === 'object' && uomData.code) {
                        item.uom_display = uomData.code;
                    } else if (typeof uomData === 'object' && uomData.name) {
                        item.uom_display = uomData.name;
                    } else {
                        this.fetchUomCode(item);
                    }
                } else if (selected.uom_id) {
                    uomFound = true;
                    item.uom = selected.uom_id;
                    this.fetchUomCode(item);
                }
                
                // If UOM not found in cache, fetch full details from API
                if (!uomFound) {
                    this.fetchItemSubGroupDetails(item, item.item_sub_group);
                }
            } else {
                 // Fallback to API if not found in list (e.g. inactive item)
                this.fetchItemSubGroupDetails(item, item.item_sub_group);
            }
        },
        fetchItemSubGroupDetails(item, itemSubGroupId) {
            let url = this.$store.state.apiendpoints.master_item_sub_group + itemSubGroupId + '/?include=uom';
            Request(this, 'GET', url).then(res => {
                if (res) {
                    if (res.description) {
                        item.description = res.description;
                    }
                    // Check for UOM in multiple possible formats
                    this.extractAndSetUOMFromResponse(item, res);
                }
            }).catch(err => {
                // Fallback without include parameter
                let fallbackUrl = this.$store.state.apiendpoints.master_item_sub_group + itemSubGroupId + '/';
                Request(this, 'GET', fallbackUrl).then(res => {
                    if (res) {
                        if (res.description) {
                            item.description = res.description;
                        }
                        if (res.uom || res.unit_of_measurement || res.uom_detail || res.uom_id) {
                            this.extractAndSetUOMFromResponse(item, res);
                        }
                    }
                });
            });
        },
        extractAndSetUOMFromResponse(item, res) {
            // First try: res.uom
            if (res.uom) {
                const uomData = res.uom;
                item.uom = (typeof uomData === 'object') ? uomData.id : uomData;
                if (typeof uomData === 'object' && uomData.code) {
                    item.uom_display = uomData.code;
                } else if (typeof uomData === 'object' && uomData.name) {
                    item.uom_display = uomData.name;
                } else {
                    this.fetchUomCode(item);
                }
            } 
            // Second try: res.unit_of_measurement
            else if (res.unit_of_measurement) {
                const uomData = res.unit_of_measurement;
                item.uom = (typeof uomData === 'object') ? uomData.id : uomData;
                if (typeof uomData === 'object' && uomData.code) {
                    item.uom_display = uomData.code;
                } else if (typeof uomData === 'object' && uomData.name) {
                    item.uom_display = uomData.name;
                } else {
                    this.fetchUomCode(item);
                }
            }
            // Third try: res.uom_detail
            else if (res.uom_detail) {
                const uomData = res.uom_detail;
                item.uom = (typeof uomData === 'object') ? uomData.id : uomData;
                if (typeof uomData === 'object' && uomData.code) {
                    item.uom_display = uomData.code;
                } else if (typeof uomData === 'object' && uomData.name) {
                    item.uom_display = uomData.name;
                } else {
                    this.fetchUomCode(item);
                }
            }
            // Fourth try: res.uom_id
            else if (res.uom_id) {
                item.uom = res.uom_id;
                this.fetchUomCode(item);
            }
        },
        fetchUomCode(item) {
            if (!item.uom) {
                return;
            }
            
            // Build URL correctly: ID should come before query parameters
            let baseUrl = this.$store.state.apiendpoints.master_uom;
            if (baseUrl.endsWith('/')) {
                baseUrl = baseUrl.slice(0, -1); // Remove trailing slash
            }
            let orgId = this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id;
            let uomUrl = baseUrl + '/' + item.uom + '/';
            if (orgId) {
                uomUrl += '?organisation=' + orgId;
            }
            
            Request(this, 'GET', uomUrl).then(uomRes => {
                if (uomRes) {
                    if (uomRes.code) {
                        this.$set(item, 'uom_display', uomRes.code);
                    } else if (uomRes.name) {
                        this.$set(item, 'uom_display', uomRes.name);
                    } else if (uomRes.abbreviation) {
                        this.$set(item, 'uom_display', uomRes.abbreviation);
                    }
                }
            }).catch(err => {
                // Silently fail
            });
        },
        validate() {
            this.validationErrors = {};
            let isValid = true;
            if (!this.formData.po_number || !this.formData.po_number.trim()) { this.validationErrors.po_number = ['PO Number is required']; isValid = false; }
            if (!this.formData.vendor) { this.validationErrors.vendor = ['Vendor is required']; isValid = false; }
            if (!this.formData.fy) { this.validationErrors.fy = ['Financial Year is required']; isValid = false; }
            if (!this.formData.po_date) { this.validationErrors.po_date = ['PO Date is required']; isValid = false; }
            
            // Validate items
            if (this.items.length === 0) {
                 this.$vs.notify({ title: 'Error', text: 'Please add at least one item', color: 'danger' });
                 return false;
            }

            for (let i = 0; i < this.items.length; i++) {
                const item = this.items[i];
                if (!item.item_sub_group || !item.qty || item.unit_rate === null) {
                    this.$vs.notify({ title: 'Error', text: `Please fill all required fields for Item ${i + 1}`, color: 'danger' });
                    isValid = false;
                    break;
                }
            }

            // Duplicate item sub group check
            const subGroupIds = this.items.map(i => i.item_sub_group).filter(Boolean);
            const uniqueIds = new Set(subGroupIds);
            if (uniqueIds.size !== subGroupIds.length) {
                this.$vs.notify({ title: 'Duplicate Item', text: 'Each Item Sub Group can only appear once in a single PO. Please remove duplicates.', color: 'danger', time: 5000 });
                isValid = false;
            }

            return isValid;
        },
        submit() {
            if (!this.validate()) return;
            
            this.submitting = true;
            this.$vs.loading();

            let orgId = this.$store.state.organisation.organisation.id;
            
            // Construct payload details
            let detailsPayload = this.items.map(item => {
                let d = {
                    item_sub_group: item.item_sub_group,
                    uom: item.uom,
                    qty: item.qty,
                    unit_rate: item.unit_rate,
                    gst_percent: item.gst_percent,
                    organisation: orgId
                };
                if (item.id) d.id = item.id;
                return d;
            });
            
            let payload = {
                po_number: this.formData.po_number,
                vendor: this.formData.vendor,
                fy: this.formData.fy,
                po_date: this.formData.po_date,
                remark: this.formData.remark,
                organisation: orgId,
                details: detailsPayload
            };

            let url = this.$store.state.apiendpoints.purchase_order;
            let method = 'POST';

            if (this.editData && this.editData.id) {
                url += `${this.editData.id}/`;
                method = 'PATCH';
                delete payload.organisation; 
            }

            Request(this, method, url, null, payload).then(res => {
                this.submitting = false;
                this.$vs.loading.close();
                const data = (res && res.data) ? res.data : res;
                const poNumber = data.po_number || '';
                const msg = this.editData ? `Purchase Order ${poNumber} Updated!` : `Purchase Order ${poNumber} Created!`;
                this.$vs.notify({ title: 'Success', text: msg, color: 'success' });
                this.$emit('success', res);
            }).catch(err => {
                this.submitting = false;
                this.$vs.loading.close();
                let errorMsg = this.editData ? 'Failed to update Purchase Order' : 'Failed to create Purchase Order';
                if (err && err.error && typeof err.error.message === 'string' && err.error.message.trim()) {
                    errorMsg = err.error.message;
                } else if (err && typeof err.message === 'string' && err.message.trim()) {
                    errorMsg = err.message;
                } else if (err && err.response && err.response.data) {
                    const data = err.response.data;
                    if (data && data.error && typeof data.error.message === 'string' && data.error.message.trim()) {
                        errorMsg = data.error.message;
                    } else if (typeof data === 'object') {
                        const messages = [];
                        for (let key in data) {
                            if (Array.isArray(data[key])) messages.push(`${key}: ${data[key].join(', ')}`);
                            else if (typeof data[key] === 'string') messages.push(`${key}: ${data[key]}`);
                        }
                        if (messages.length > 0) errorMsg = messages.join(' | ');
                    }
                }
                
                this.$vs.notify({ title: 'Error', text: errorMsg, color: 'danger' });
                console.error(err);
            });
        }
    }
}
</script>

<style>
/* Method to remove item needs update for pagination */
/* Not modifying removeItem in this chunk, assuming method is standard splcie. Wait, need to fix removeItem index! */

/* CSS for Compact Table Inputs */
.order-items-table .vs-con-tbody {
    overflow: visible !important;
}
.order-items-table .vs-table--tbody-table {
    border-collapse: separate;
    border-spacing: 0 0.5rem;
}
.order-items-table td {
    vertical-align: middle !important;
}
.order-items-table .vs-inputx {
    padding: 0.4rem !important;
    min-height: 32px !important;
    height: 32px !important;
}
.order-items-table .vs-con-input {
    margin: 0 !important;
}
.order-items-table .v-select {
    margin: 0 !important;
}
.order-items-table .vs__dropdown-toggle {
    padding: 0 0 4px 0 !important;
    min-height: 32px !important;
    height: 32px !important;
}
.order-items-table .vs__dropdown-menu {
    max-height: 160px !important;
    overflow-y: auto !important;
}
.order-items-table .vs__selected-options {
    padding: 0 2px;
}
.order-items-table .vs__search::placeholder {
    line-height: 30px;
}
</style>
